import { FC, useMemo } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import Routes from '@/router/Routes.ts';
import { useAuthContext } from '@/context/AuthContext.tsx';
import Middleware from '@/router/middleware/index.tsx';
import useFamilyOnboarding from '@/api/useFamilyOnboarding.ts';

const FamilyOnboarding: FC = () => {
  const { isLoading, session, currentUser, isLoadingUser } = useAuthContext();
  const location = useLocation();

  const email = session?.user?.email?.toLowerCase() ?? null;
  const isFamilyMember = currentUser?.role === 'family_member';
  const isOnboardingRoute = location.pathname === Routes.ONBOARDING;

  // Only fetch onboarding data for family members
  const { data, isLoading: isLoadingOnboarding, isError } = useFamilyOnboarding(email, isFamilyMember);

  const shouldRedirectToOnboarding = useMemo(() => {
    // Wait for user data to load
    if (isLoadingUser) return false;
    // If not a family member, don't force onboarding
    if (!isFamilyMember) return false;
    // If already on onboarding page, don't redirect
    if (isOnboardingRoute) return false;
    // Wait for data to load
    if (isLoadingOnboarding || isError) return false;
    if (!email) return false;
    // Redirect to onboarding if no data exists
    return !data;
  }, [data, email, isError, isFamilyMember, isLoadingOnboarding, isOnboardingRoute, isLoadingUser]);

  // Only show loading for family members checking onboarding status
  const isMiddlewareLoading = isLoading || isLoadingUser || (isFamilyMember && isLoadingOnboarding);

  return (
    <Middleware isLoading={isMiddlewareLoading}>
      {shouldRedirectToOnboarding ? <Navigate to={Routes.ONBOARDING} replace /> : <Outlet />}
    </Middleware>
  );
};

export default FamilyOnboarding;

